import './ToolTipTitle.scss';
import React, { memo, ReactNode, useContext, useMemo } from 'react';
import Tooltip from '../Tooltip';
import { MdHelpOutline } from 'react-icons/md';
import AutoLinkText from '../AutoLinkText';
import { decodeHtmlEntities } from '../../lib/html';
import { FieldA11yContext } from '../../context/FieldA11yContext';

export interface IToolTipTitleProps {
    /** Текст подсказки: URL в строке становятся ссылками; разметка выводится как есть. */
    title?: ReactNode;
    /**
     * Не превращать URL строки в ссылки.
     * @default false
     */
    disableLinks?: boolean;
    /**
     * Подпись поля для имени значка у диктора: «Подсказка: <подпись>». По умолчанию — строковая подпись поля, рядом с
     * которым стоит значок (`FormGroup`, `labelAdornment` флажка). Значок внутри `<label>` вошёл бы в имя поля: рядом с
     * подписью флажка его ставят через `labelAdornment`.
     */
    label?: string;
}

// Подсказка висит почти на каждом поле карточки, а Tooltip разбирает своё содержимое через
// renderToStaticMarkup + DOMPurify. Мемоизация здесь не косметическая: без memo элемент
// <AutoLinkText> пересоздавался на каждый рендер поля, инвалидируя useMemo внутри Tooltip, —
// и разбор HTML шёл заново для каждого поля при каждом проходе формы.
//
// Значок — кнопка: до подсказки доходят Tab (открывает фокусом), Enter и Space, Escape скрывает. Диктору строковый
// текст — постоянное описание кнопки (aria-description): его прочтут и без показа подсказки, а пока она открыта,
// описание даёт сама подсказка (aria-describedby сильнее). Мышью всё как раньше: подсказку показывает наведение.
const ToolTipTitle: React.FC<IToolTipTitleProps> = memo(({ label: ownLabel, ...props }: IToolTipTitleProps) => {
    const { title, disableLinks = false } = props;
    const field = useContext(FieldA11yContext);
    const label = ownLabel ?? field?.labelText;
    const content = useMemo(
        () => (typeof title === 'string' ? <AutoLinkText text={title} disableLinks={disableLinks} /> : title),
        [title, disableLinks],
    );
    const description = useMemo(
        () =>
            typeof title === 'string'
                ? decodeHtmlEntities(title.replace(/<[^>]*>/g, ' '))
                      .replace(/\s+/g, ' ')
                      .trim() || undefined
                : undefined,
        [title],
    );

    return title ? (
        <div className="field-wrapper-title">
            <Tooltip
                openDelay={100}
                background="black"
                position="bottom"
                maxWidth="40ch"
                useInnerHtml
                wrapTarget={false}
                openOnPress
                label={content}
            >
                {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- aria-description (ARIA 1.3) глобальный, aria-query не знает его у button */}
                <button
                    type="button"
                    className="field-wrapper-title__trigger"
                    aria-label={label ? `Подсказка: ${label}` : 'Подсказка'}
                    aria-description={description}
                >
                    <MdHelpOutline size="16" aria-hidden />
                </button>
            </Tooltip>
        </div>
    ) : (
        <></>
    );
});

ToolTipTitle.displayName = 'ToolTipTitle';

export default ToolTipTitle;
